import { format } from "date-fns";
import { AlertTriangle, ArrowRight, XCircle } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useInsuranceRecord } from "../../../hooks/use-api";
import { Button } from "../../ui/button";
import { CategoryBadge } from "../category-badge";

interface RecordBannersProps {
  cancelled: boolean;
  cancelledDate: string | null;
  onUncancel?: () => void;
  supersededById: number | null;
  supersededByAddress: string | null;
  supersedesId: number | null;
  supersedesAddress: string | null;
}

export function RecordBanners({
  cancelled,
  cancelledDate,
  onUncancel,
  supersededById,
  supersededByAddress,
  supersedesId,
  supersedesAddress,
}: RecordBannersProps): React.ReactElement | null {
  const { id } = useParams<{ id: string }>();
  const { data } = useInsuranceRecord(id);
  const propertyCategory = data?.record?.propertyCategory;

  if (!cancelled && !supersededById && !supersedesId && !propertyCategory) return null;

  return (
    <div className="space-y-2">
      {!!propertyCategory && (
        <div>
          <CategoryBadge category={propertyCategory as string} fullName />
        </div>
      )}
      {cancelled && (
        <div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-destructive" />
            <span className="text-sm font-medium">
              This record was cancelled{cancelledDate ? ` on ${format(new Date(cancelledDate), "d MMMM yyyy")}` : ""}
            </span>
          </div>
          {onUncancel && (
            <Button variant="outline" size="sm" onClick={onUncancel} data-testid="uncancel-button">
              Un-cancel
            </Button>
          )}
        </div>
      )}
      {supersededById && (
        <div className="flex items-center gap-2 rounded-md border border-orange-300/50 bg-orange-50 px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <span className="text-sm">
            This record has been superseded by{" "}
            <Link to={`/insurance/${supersededById}`} className="font-medium text-primary hover:underline">
              {supersededById} – {supersededByAddress}
            </Link>
          </span>
          <Link to={`/insurance/${supersededById}`}>
            <ArrowRight className="h-4 w-4 text-orange-600" />
          </Link>
        </div>
      )}
      {supersedesId && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-4 py-3">
          <span className="text-sm text-muted-foreground">
            This record supersedes{" "}
            <Link to={`/insurance/${supersedesId}`} className="font-medium text-primary hover:underline">
              {supersedesId} – {supersedesAddress}
            </Link>
          </span>
        </div>
      )}
    </div>
  );
}
